import { Button, Modal, ServiceError } from "@shapr3d/frontend-lib";
import { toast } from "react-hot-toast";

import useNavigation from "../../../../hooks/navigation";
import useAccount from "../../../../hooks/useAccount";
import { useResendVerificationEmail } from "../../../../hooks/useAuth";
import { useTracker } from "../../../../hooks/useTracker";
import { ServiceErrorCode } from "../../../../types";
import { GenericErrorModal } from "../../../Modal/Modal";
import { NewZendeskTicketLink } from "../../../NewZendeskTicketLink/NewZendeskTicketLink";

import style from "./CreateCheckoutErrorModal.module.css";

const ResendEmailVerification: React.FC = () => {
  const tracker = useTracker();
  const navigation = useNavigation();
  const { data: accountData } = useAccount();

  const { mutate: mutateResendVerificationEmail, isPending: isResendVerificationEmailPending } =
    useResendVerificationEmail();

  return (
    <div className={style.ctaRow}>
      <Button
        disabled={isResendVerificationEmailPending}
        onClick={async () => {
          tracker.trackRequestVerificationEmailClicked({ location: "checkout" });
          mutateResendVerificationEmail();
          toast.success(
            <span>
              {`Verification email sent to `}
              <b>{accountData?.email}</b>
            </span>
          );
          navigation.goToProfile();
        }}
      >
        Resend verification email
      </Button>
    </div>
  );
};

export const CheckoutErrorModal: React.FC<{
  error: Error | null;
  open: boolean;
  onClose: () => void;
}> = ({ error, open, onClose }) => {
  const tracker = useTracker();

  if (error instanceof ServiceError) {
    if (error.hasCode(ServiceErrorCode.UserAlreadyHasSubscription)) {
      tracker.trackCheckoutFlowLoadError("already_has_subscription");
      return (
        <Modal title="Checkout error" open={open} onClose={onClose}>
          <span>{"You already have a subscription."}</span>
        </Modal>
      );
    }
    if (error.hasCode(ServiceErrorCode.UnsupportedCountry)) {
      tracker.trackCheckoutFlowLoadError("unsupported_country");
      return (
        <Modal title="Checkout error" open={open} onClose={onClose}>
          <span>
            {
              "Direct subscriptions are not supported in your country. Try subscribing from the Apple App Store instead."
            }
          </span>
        </Modal>
      );
    }
    if (error.hasCode(ServiceErrorCode.EmailNotVerified)) {
      tracker.trackCheckoutFlowLoadError("unverified_email");
      return (
        <Modal title="Checkout error" open={open} size="small" onClose={onClose}>
          <div className={style.verifyEmailContainer}>
            <p>{"Please verify your email address to complete the checkout."}</p>
            <ResendEmailVerification />
          </div>
        </Modal>
      );
    }

    if (error.hasCode(ServiceErrorCode.CheckoutNotAllowed)) {
      tracker.trackCheckoutFlowLoadError("not_allowed");
      return (
        <Modal title="Too many attempts" open={open} onClose={onClose}>
          Please try again later, or submit a support ticket via{" "}
          <NewZendeskTicketLink>this link</NewZendeskTicketLink>.
        </Modal>
      );
    }
  }

  if (open) {
    tracker.trackCheckoutFlowLoadError("unknown");
  }
  return <GenericErrorModal open={open} onClose={onClose} />;
};
